import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Modal, Select } from 'antd';
import { PAGINATION, type Paginated, type PolicyListItemDto } from '@pams/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

/**
 * "Copy command roles from an existing policy."
 *
 * ⚠️ THIS IS THE WHOLE MITIGATION FOR PER-POLICY COMMAND ROLES.
 *
 * Roles are defined per policy so a Republic Day arrangement can declare its own set.
 * The cost is that "In Charge" and "Incharge" can become two different things for ever.
 * This is what makes SAMENESS the easy path — and it deliberately sends only the source
 * policy's id, never its labels: the server copies each role's KEY verbatim, so a role
 * whose label was edited last month arrives under the key it already had rather than
 * under a freshly-minted one. Posting the labels back would quietly break exactly the
 * comparability this exists to preserve.
 */
export const CopyCommandRolesModal = ({
  open,
  excludePolicyId,
  onClose,
  onPick,
}: {
  open: boolean;
  excludePolicyId?: string;
  onClose: () => void;
  onPick: (policyId: string) => void;
}) => {
  const [selected, setSelected] = useState<string | undefined>(undefined);

  const { data, isFetching } = useQuery({
    queryKey: queryKeys.policies.list({ purpose: 'copy-command-roles' }),
    queryFn: ({ signal }) =>
      api.get<Paginated<PolicyListItemDto>>('/policies', {
        query: { limit: PAGINATION.MAX_LIMIT, sortBy: 'name', sortOrder: 'asc', show: 'all' },
        signal,
      }),
    enabled: open,
  });

  const options = (data?.items ?? [])
    .filter((policy) => policy.id !== excludePolicyId)
    .map((policy) => ({ value: policy.id, label: policy.name }));

  return (
    <Modal
      open={open}
      title="Copy command roles from another policy"
      okText="Copy roles"
      okButtonProps={{ disabled: !selected }}
      onCancel={() => {
        setSelected(undefined);
        onClose();
      }}
      onOk={() => {
        /*
         * ⚠️ THE RESET BELONGS TO THE COMMIT **AND** TO THE DISMISSAL, IN THE SAME FUNCTION,
         * UNCONDITIONALLY. This modal is mounted by `PolicyBuilder` at ALL times — `<Copy…
         * open={copying} />` rather than `{copying && <Copy… />}` — so `selected` outlives
         * every close, and reopening showed the previous choice already picked.
         *
         * ⚠️ [amended C3c §11b] `PostTypeOrderField` WAS CITED HERE AS THE MODEL OF CORRECT, AND
         * ITS RESET HAD NEVER WORKED. It set its `value` prop from `undefined` to `undefined`,
         * which rc-util reads as UNCONTROLLED — so the Select kept its own `innerValue` and went
         * on displaying the last post type chosen. That belief is why the defect survived. THIS
         * modal is genuinely correct, and for a different reason: `selected` transitions from a
         * real id to `undefined`, which is a change React and rc-util both see.
         *
         * The reset still belongs to BOTH handlers, unconditionally. The conditionally-mounted modals
         * (`MergePlaceModal`, `MoveUnitModal`, `TransferOfficerModal`) are safe only because
         * unmounting destroys their state — which is luck, not design.
         */
        setSelected(undefined);
        if (selected) onPick(selected);
      }}
    >
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="The roles are copied with their stable keys."
        description="That is what keeps two policies' command roles comparable — a role copied from another policy stays the same role for reporting, even after either label is edited."
      />
      <Select
        value={selected}
        onChange={(next: string | undefined) => setSelected(next)}
        loading={isFetching}
        showSearch
        optionFilterProp="label"
        placeholder="Choose a policy"
        className="w-full"
        data-testid="copy-roles-source"
        options={options}
        notFoundContent={options.length === 0 ? 'There is no other policy to copy from yet.' : undefined}
      />
    </Modal>
  );
};
